import React from 'react';
import PropTypes from 'prop-types';
import { ROUTE_PATH, ROUTE_NAME } from 'utils/constants/router';
import { formRoutePath } from 'functions/formRoutePath';
import { getStatusBadgeColors } from './trialCardMsg.utils';
import styles from './trialCardMsg.module.css';

export const TrialCardMsg = ({ content }) => {
  const {
    nct,
    summary,
    phase,
    objectives,
    administration,
    status,
    location,
  } = content;
  const badgeColors = getStatusBadgeColors(status);

  const goals = Array.isArray(objectives) && objectives.length > 0
    ? objectives.filter((g) => typeof g === 'string' && g.trim() !== '')
    : [];

  const distanceMiles = typeof location?.distanceMiles === 'number' && !Number.isNaN(location.distanceMiles)
    ? String(Math.round(Number(location.distanceMiles)))
    : 'Unknown';

  const treatmentType = Array.isArray(administration) && administration.length > 0
    ? administration.filter(Boolean).join(', ')
    : 'Unknown';

  const trialRoutePath = nct
    ? formRoutePath(ROUTE_PATH[ROUTE_NAME.TRIAL], { nctNumber: encodeURIComponent(nct) })
    : null;

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <h3 className={styles.title}>{`Trial ${nct || 'Unknown'}`}</h3>
        {status ? (
          <span
            className={styles.statusBadge}
            style={{ backgroundColor: badgeColors.bg, color: badgeColors.text }}
          >
            {status}
          </span>
        ) : null}
      </div>

      <p className={styles.meta}>
        <strong className={styles.label}>Phase:</strong>
        <span className={styles.phaseValue}>{phase || 'Unknown'}</span>
      </p>

      <div className={styles.section}>
        <strong className={styles.sectionTitle}>Summary</strong>
        <p className={styles.summaryText}>{summary || 'No summary available.'}</p>
      </div>

      <div className={styles.section}>
        <strong className={styles.sectionTitle}>Key Goals</strong>
        <ul className={styles.goalsList}>
          {goals.length ? goals.map((goal, idx) => (<li key={idx}>{goal}</li>)) : (<li>No key goals listed</li>)}
        </ul>
      </div>

      <div className={styles.details}>
        <p className={styles.detailRow}>
          <strong className={styles.label}>Treatment Type:</strong>
          <span className={styles.treatmentValue}>{treatmentType}</span>
        </p>
        <p className={styles.detailRow}>
          <strong className={styles.label}>Closest Location:</strong>
          <span>{`${location?.city || 'Unknown'}, ${location?.state || 'Unknown'}`}</span>
          <span className={styles.locationNote}>{`(approx. ${distanceMiles} miles)`}</span>
        </p>
      </div>
      {!!trialRoutePath && (
        <div className={styles.cta}>
          <a
            href={trialRoutePath}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.ctaLink}
          >
            View Full Details
          </a>
        </div>
      )}
    </div>
  );
};

TrialCardMsg.propTypes = {
  content: PropTypes.shape({
    nct: PropTypes.string,
    title: PropTypes.string,
    summary: PropTypes.string,
    phase: PropTypes.string,
    objectives: PropTypes.arrayOf(PropTypes.string),
    administration: PropTypes.arrayOf(PropTypes.string),
    status: PropTypes.string,
    location: PropTypes.shape({
      city: PropTypes.string,
      state: PropTypes.string,
      distanceMiles: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.oneOf([null]),
      ]),
    }),
    isSponsored: PropTypes.bool,
    sponsorName: PropTypes.string,
    link: PropTypes.shape({
      clinicalTrialsGov: PropTypes.string,
    }),
  }).isRequired,
};
